import { Person } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
    AppBar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Divider,
    Grid,
    IconButton,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material';
import AppointmentNumberSvg from '../utility/Svg/AppointmentNumberSvg';
import CalendarSvg from '../utility/Svg/CalendarSvg';
import CarSvgV4 from '../utility/Svg/CarSvgV4';
import DropoffSvg from '../utility/Svg/DropoffSvg';
import ServiceSvg from '../utility/Svg/ServiceSvg';

const DEFAULT_APPOINTMENT_DETAIL = {
    customerName: 'Momin Sheikh',
    appointmentId: '1220-01',
    vehicle: '2022 BMW 3 Series',
    dateTime: '21-Feb-2025 - 11:30 AM',
    servicesCount: '2 Services',
    status: 'Vehicle Dropped Off',
};

const ICON_COLOR = '#006CDD';
const ICON_SIZE = 20;

const DetailRow = ({ icon, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon}
        <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.87)', fontSize: 16, fontWeight: 400 }}>
            {label}
        </Typography>
    </Box>
);

const AppointmentCard = ({ detail, selected, onClick }) => (
    <Box
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
            }
        }}
        sx={{
            padding: '12px',
            borderRadius: '8px',
            border: `${selected ? '2px solid #0066E1' : '1px solid rgba(0, 0, 0, 0.10)'} !important`,
            backgroundColor: selected ? 'rgba(0, 102, 225, 0.04)' : '#FFF',
            cursor: 'pointer',
            marginBottom: '8px',
            '&:hover': { backgroundColor: selected ? 'rgba(0, 102, 225, 0.08)' : 'rgba(0, 0, 0, 0.02)' },
        }}
    >
        <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
                <DetailRow icon={<Person sx={{ color: ICON_COLOR, fontSize: ICON_SIZE }} />} label={detail.customerName} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <DetailRow icon={<AppointmentNumberSvg color={ICON_COLOR} size={ICON_SIZE} />} label={detail.appointmentId} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <DetailRow icon={<CarSvgV4 color={ICON_COLOR} size={ICON_SIZE} />} label={detail.vehicle} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <DetailRow icon={<CalendarSvg color={ICON_COLOR} size={ICON_SIZE} />} label={detail.dateTime} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <DetailRow icon={<ServiceSvg color={ICON_COLOR} size={ICON_SIZE} />} label={detail.servicesCount} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <DetailRow icon={<DropoffSvg color={ICON_COLOR} size={ICON_SIZE} />} label={detail.status} />
            </Grid>
        </Grid>
    </Box>
);

const LinkedAppointmentDialog = ({
    open = false,
    onClose,
    isAlreadyLinked = false,
    appointmentDetail,
    appointmentsList,
    selectedAppointmentId,
    onSelectAppointment,
    onSkip,
    onLinkOnly,
    onLinkAndComplete,
    onMarkComplete,
    markCompleteLoading = false,
    linkAndCompleteLoading = false,
    linkButtonsDisabled = false,
    hideCompleteButton = false,
}) => {
    const isMultiple = Array.isArray(appointmentsList) && appointmentsList.length > 1;
    const detail = { ...DEFAULT_APPOINTMENT_DETAIL, ...(appointmentDetail || (appointmentsList?.length === 1 ? appointmentsList[0] : {})) };
    const isLoading = markCompleteLoading || linkAndCompleteLoading;

    const handleSkip = () => {
        onSkip?.();
        onClose?.();
    };

    const handleLinkOnly = () => {
        onLinkOnly?.();
    };

    const handleLinkAndComplete = () => {
        onLinkAndComplete?.();
    };

    const handleMarkComplete = () => {
        onMarkComplete?.();
        // Parent closes dialog after async success (e.g. status update API)
    };

    const title = isAlreadyLinked ? '🔗 Mark Linked Appointment as Complete?' : '🔗 Link Invoice to Appointment?';
    const introText = isAlreadyLinked
        ? 'This invoice is linked to the appointment below. Mark it as complete?'
        : isMultiple
          ? 'We found multiple active appointments for this customer. Please select the matching appointment to link with this invoice.'
          : 'We found an existing appointment for this customer. Would you like to link this invoice and mark the appointment as complete?';

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (isLoading) return;
                if (reason !== 'backdropClick') onClose?.(event, reason);
            }}
            aria-labelledby="linked-appointment-title"
            aria-describedby="linked-appointment-description"
            className="commonDialog commonDialog__600"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '0%',
                    left: '0%',
                    transform: 'translate(-0%, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    width: '100%',
                    border: 'none',
                    borderRadius: '8px 8px 0px 0px',
                }}
            >
                <AppBar
                    color=""
                    variant="outlined"
                    style={{
                        width: 'inherit',
                        borderRadius: '8px 8px 0px 0px !important',
                    }}
                    sx={{
                        padding: '0 16px!important',
                    }}
                >
                    <Toolbar variant="regular" sx={{ color: 'black' }}>
                        <Typography id="linked-appointment-title" variant="h6" width={'98%'} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {title}
                        </Typography>
                        <Stack direction={'row'} spacing={0}>
                            <IconButton onClick={() => onClose?.()} size="small" aria-label="close" disabled={isLoading}>
                                <CloseIcon sx={{ fill: 'black', fontSize: 32 }} />
                            </IconButton>
                        </Stack>
                    </Toolbar>
                </AppBar>
            </Box>

            <DialogContent sx={{ margin: '24px', marginTop: '88px' }}>
                <DialogContentText id="linked-appointment-description" sx={{ color: 'black' }}>
                    <div style={{ color: '#000' }}>{introText}</div>

                    <Divider sx={{ margin: '20px 0' }} />

                    {isMultiple ? (
                        <Box>
                            {appointmentsList.map((appt) => (
                                <AppointmentCard
                                    key={appt._id}
                                    detail={{
                                        customerName: appt.customerName ?? '—',
                                        appointmentId: appt.appointmentId ?? '—',
                                        vehicle: appt.vehicle ?? '—',
                                        dateTime: appt.dateTime ?? '—',
                                        servicesCount: appt.servicesCount ?? '0 Services',
                                        status: appt.status ?? '—',
                                    }}
                                    selected={selectedAppointmentId === appt._id}
                                    onClick={() => onSelectAppointment?.(appt._id)}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                                <DetailRow icon={<Person sx={{ color: ICON_COLOR, fontSize: ICON_SIZE }} />} label={detail.customerName} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailRow icon={<AppointmentNumberSvg color={ICON_COLOR} size={ICON_SIZE} />} label={detail.appointmentId} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailRow icon={<CarSvgV4 color={ICON_COLOR} size={ICON_SIZE} />} label={detail.vehicle} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailRow icon={<CalendarSvg color={ICON_COLOR} size={ICON_SIZE} />} label={detail.dateTime} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailRow icon={<ServiceSvg color={ICON_COLOR} size={ICON_SIZE} />} label={detail.servicesCount} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailRow icon={<DropoffSvg color={ICON_COLOR} size={ICON_SIZE} />} label={detail.status} />
                            </Grid>
                        </Grid>
                    )}
                </DialogContentText>
            </DialogContent>

            <DialogActions
                sx={{
                    padding: '16px',
                    justifyContent: 'flex-end',
                    gap: 1,
                    borderTop: '1px solid rgba(0, 0, 0, 0.10)',
                    marginTop: 0,
                }}
            >
                <Button onClick={handleSkip} variant="outlined" color="primary" size="medium" disabled={isLoading}>
                    SKIP
                </Button>
                {!isAlreadyLinked && (
                    <Button onClick={handleLinkOnly} variant="outlined" color="primary" size="medium" disabled={linkButtonsDisabled || isLoading}>
                        LINK ONLY
                    </Button>
                )}
                {isAlreadyLinked ? (
                    <Button onClick={handleMarkComplete} variant="contained" color="primary" size="medium" disabled={isLoading}>
                        {markCompleteLoading ? 'Updating...' : 'MARK AS COMPLETE'}
                    </Button>
                ) : (
                    !hideCompleteButton && (
                        <Button onClick={handleLinkAndComplete} variant="contained" color="primary" size="medium" disabled={linkButtonsDisabled || isLoading}>
                            {linkAndCompleteLoading ? 'Updating...' : 'LINK AND COMPLETE APPOINTMENT'}
                        </Button>
                    )
                )}
            </DialogActions>
        </Dialog>
    );
};

export default LinkedAppointmentDialog;
export { DEFAULT_APPOINTMENT_DETAIL };
